"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";
import {
  captureLandingEvent,
  getSelectedLandingPlanFromUrl,
  landingAnalyticsEvents,
  normalizeLandingPlan,
} from "./landingAnalytics";

const TRACKED_PAGE_TYPES: Record<string, string> = {
  "/": "home",
  "/pricing": "pricing",
  "/sign-up": "sign_up",
  "/sign-up/thanks": "thank_you",
};

const SCROLL_MILESTONES = [25, 50, 75, 90, 100] as const;

type AnalyticsCustomEventDetail = Record<string, unknown> & {
  event?: unknown;
};

function getPageType(pathname: string | null) {
  if (!pathname) return null;
  return TRACKED_PAGE_TYPES[pathname] ?? null;
}

function getElementText(element: Element) {
  return (element.textContent ?? "").replace(/\s+/g, " ").trim().slice(0, 160) || null;
}

function getElementHref(element: Element) {
  if (element instanceof HTMLAnchorElement) return element.href;
  return null;
}

function getDestinationProperties(element: Element) {
  const href = getElementHref(element);
  if (!href) return { href: null, destination_path: null, destination_host: null, is_external: null };

  try {
    const url = new URL(href);
    return {
      href: url.toString(),
      destination_path: url.pathname,
      destination_host: url.host,
      is_external: url.origin !== window.location.origin,
    };
  } catch {
    return { href, destination_path: null, destination_host: null, is_external: null };
  }
}

function getSectionId(element: Element) {
  const section = element.closest<HTMLElement>("[data-analytics-section]");
  return section?.dataset.analyticsSection ?? null;
}

function getPlanProperties(element: Element) {
  const planCard = element.closest<HTMLElement>("[data-analytics-plan]");
  let plan = normalizeLandingPlan(planCard?.dataset.analyticsPlan);
  let price = planCard?.dataset.analyticsPrice ?? null;
  const featured = planCard?.dataset.analyticsFeatured === "true";

  const href = getElementHref(element);
  if (href) {
    try {
      const url = new URL(href);
      plan = plan ?? normalizeLandingPlan(url.searchParams.get("plan"));
      price = price ?? url.searchParams.get("price");
    } catch {
      // Ignore invalid URLs.
    }
  }

  return { plan, price, featured: planCard ? featured : null };
}

function getCtaType(ctaId: string, plan: string | null) {
  if (plan || ctaId.startsWith("pricing-")) return "pricing_plan";
  if (ctaId.includes("talk") || ctaId.includes("book") || ctaId.includes("chat")) return "secondary";
  return "primary";
}

export default function LandingAnalyticsTracker() {
  const pathname = usePathname();

  useEffect(
    function trackPageView() {
      const pageType = getPageType(pathname);
      if (!pageType) return;

      const selectedPlan = getSelectedLandingPlanFromUrl();

      captureLandingEvent(landingAnalyticsEvents.pageViewed, {
        page_type: pageType,
        selected_plan: selectedPlan,
      });

      if (pageType === "thank_you") {
        captureLandingEvent(landingAnalyticsEvents.thankYouViewed, {
          page_type: pageType,
          selected_plan: selectedPlan,
        });
      }
    },
    [pathname]
  );

  useEffect(
    function trackCtaClicks() {
      const pageType = getPageType(pathname);
      if (!pageType) return;

      function onClick(event: MouseEvent) {
        const target = event.target as Element | null;
        if (!target) return;

        const cta = target.closest<HTMLElement>("a[data-cta], button[data-cta]");
        if (!cta) return;

        const ctaId = cta.dataset.cta ?? "";
        if (!ctaId) return;

        const { plan, price, featured } = getPlanProperties(cta);

        captureLandingEvent(landingAnalyticsEvents.ctaClicked, {
          page_type: pageType,
          cta_id: ctaId,
          cta_text: getElementText(cta),
          cta_location: getSectionId(cta) ?? ctaId.split("-")[0] ?? null,
          cta_type: getCtaType(ctaId, plan),
          section_id: getSectionId(cta),
          plan,
          price,
          featured,
          selected_plan: getSelectedLandingPlanFromUrl(),
          ...getDestinationProperties(cta),
        });
      }

      document.addEventListener("click", onClick);
      return function cleanup() {
        document.removeEventListener("click", onClick);
      };
    },
    [pathname]
  );

  useEffect(
    function trackProofSourceClicks() {
      const pageType = getPageType(pathname);
      if (!pageType) return;

      function onClick(event: MouseEvent) {
        const target = event.target as Element | null;
        if (!target) return;

        const source = target.closest<HTMLAnchorElement>("a[data-analytics-proof-source]");
        if (!source) return;

        captureLandingEvent(landingAnalyticsEvents.proofSourceClicked, {
          page_type: pageType,
          section_id: getSectionId(source),
          source_id: source.dataset.analyticsProofSource ?? null,
          source_theme: source.dataset.analyticsProofTheme ?? null,
          source_stat: source.dataset.analyticsProofStat ?? null,
          source_label: source.dataset.analyticsProofSourceLabel ?? null,
          selected_plan: getSelectedLandingPlanFromUrl(),
          ...getDestinationProperties(source),
        });
      }

      document.addEventListener("click", onClick);
      return function cleanup() {
        document.removeEventListener("click", onClick);
      };
    },
    [pathname]
  );

  useEffect(
    function trackSectionAndPlanImpressions() {
      const pageType = getPageType(pathname);
      if (!pageType || typeof IntersectionObserver === "undefined") return;

      const seenSections = new Set<string>();
      const seenPlans = new Set<string>();

      const observer = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const element = entry.target as HTMLElement;
            const sectionId = element.dataset.analyticsSection;
            const plan = normalizeLandingPlan(element.dataset.analyticsPlan);

            if (sectionId && !seenSections.has(sectionId)) {
              seenSections.add(sectionId);
              captureLandingEvent(landingAnalyticsEvents.sectionViewed, {
                page_type: pageType,
                section_id: sectionId,
              });
            }

            if (plan && !seenPlans.has(plan)) {
              seenPlans.add(plan);
              captureLandingEvent(landingAnalyticsEvents.pricingPlanViewed, {
                page_type: pageType,
                plan,
                price: element.dataset.analyticsPrice ?? null,
                featured: element.dataset.analyticsFeatured === "true",
              });
            }

            if ((!sectionId || seenSections.has(sectionId)) && (!plan || seenPlans.has(plan))) {
              observer.unobserve(element);
            }
          }
        },
        { rootMargin: "0px 0px -10% 0px", threshold: 0.4 }
      );

      document
        .querySelectorAll<HTMLElement>("[data-analytics-section], [data-analytics-plan]")
        .forEach(element => observer.observe(element));

      return function cleanup() {
        observer.disconnect();
      };
    },
    [pathname]
  );

  useEffect(
    function trackFaqOpen() {
      const pageType = getPageType(pathname);
      if (!pageType) return;

      const seenQuestions = new Set<string>();
      const details = Array.from(
        document.querySelectorAll<HTMLDetailsElement>("details[data-analytics-faq-question]")
      );

      function onToggle(event: Event) {
        const detail = event.currentTarget as HTMLDetailsElement | null;
        if (!detail?.open) return;

        const question = detail.dataset.analyticsFaqQuestion;
        if (!question || seenQuestions.has(question)) return;

        seenQuestions.add(question);
        captureLandingEvent(landingAnalyticsEvents.faqOpened, {
          page_type: pageType,
          question,
          section_id: getSectionId(detail),
        });
      }

      details.forEach(detail => detail.addEventListener("toggle", onToggle));
      return function cleanup() {
        details.forEach(detail => detail.removeEventListener("toggle", onToggle));
      };
    },
    [pathname]
  );

  useEffect(
    function trackScrollDepth() {
      const pageType = getPageType(pathname);
      if (!pageType) return;

      const reached = new Set<number>();
      const startedAt = Date.now();

      function getScrollDepth() {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollable <= 0) return 100;
        return Math.min(100, Math.round((window.scrollY / scrollable) * 100));
      }

      function checkDepth() {
        const depth = getScrollDepth();
        for (const milestone of SCROLL_MILESTONES) {
          if (depth < milestone || reached.has(milestone)) continue;

          reached.add(milestone);
          captureLandingEvent(landingAnalyticsEvents.scrollDepthReached, {
            page_type: pageType,
            depth: milestone,
            time_to_depth_ms: Date.now() - startedAt,
          });
        }
      }

      window.addEventListener("scroll", checkDepth, { passive: true });
      window.addEventListener("resize", checkDepth);
      window.setTimeout(checkDepth, 250);

      return function cleanup() {
        window.removeEventListener("scroll", checkDepth);
        window.removeEventListener("resize", checkDepth);
      };
    },
    [pathname]
  );

  useEffect(function forwardCustomAnalyticsEvents() {
    function onAnalyticsEvent(event: Event) {
      const detail = (event as CustomEvent<AnalyticsCustomEventDetail>).detail;
      if (!detail || typeof detail.event !== "string") return;

      const { event: eventName, ...properties } = detail;
      captureLandingEvent(eventName, properties);
    }

    window.addEventListener("analytics", onAnalyticsEvent);
    return function cleanup() {
      window.removeEventListener("analytics", onAnalyticsEvent);
    };
  }, []);

  return null;
}
